/* eslint-disable max-len */
export class AssetType {
    /** Contains a list of AssetType instances. */
    static list = [];
    /** Contains a list of the AssetType names. */
    static nameList = [];
    /** Associates the names of the AssetType names with AssetTypes instances. */
    static map = {};
    /** Contains a list of all the unique group names for AssetTypes. */
    static groupList = [];
    /** Map group names to lists of AssetType instances with that group name. */
    static byGroup = {};
    options;
    name;
    title;
    description;
    icon;
    iconText;
    hiddenFromInterface;
    group;
    parallelGroups;
    propertiesList;
    properties;
    constructor(options) {
        this.options = options;
        this.name = options.name;
        this.icon = options.icon;
        this.iconText = options.iconText || "";
        this.title = options.title;
        this.group = options.group;
        this.description = options.description;
        this.hiddenFromInterface = !!options.hiddenFromInterface;
        this.parallelGroups = options.parallelGroups || [];
        this.propertiesList = options.propertiesList;
        this.properties = {};
        for (let i = 0; i < options.propertiesList.length; i++) {
            const property = options.propertiesList[i];
            this.properties[property.key] = property;
        }
        AssetType.list.push(this);
        AssetType.nameList.push(this.name);
        AssetType.map[this.name] = this;
        if (AssetType.groupList.indexOf(this.group) < 0) {
            AssetType.groupList.push(this.group);
        }
        if (!AssetType.byGroup[this.group]) {
            AssetType.byGroup[this.group] = [];
        }
        AssetType.byGroup[this.group].push(this);
    }
}
/** *******************************************************************
 * Spec
 *********************************************************************
 *
 * "name": Unique key for identifying the asset type
 * "title": Very short descriptive name for use in frontend UI
 * "description": Longer description to use as help in frontend UI
 * "icon": "[<icon-class>, <icon-name>]" font awesome icon class
 *     and name to use in frontend UI. NOTE: This is overridden
 *     by components/base-elements/AssetIcon.js for assets
 *     with composite icons
 * "hiddenFromInterface": If truthy, this assetType is supported but
 *     will not be available in the frontend UI
 * "propertiesList": {
 *     "key": Unique key for identifying the property type
 *     "type": Used by the backend for conversion to and from the DB
 *     "inputType": Used by the frontend to determine form field type
 *     "default": Default value used by frontend.
 *     "title": Very short descriptive name for use in frontend UI
 *     "description": Longer description to use as help in frontend UI
 *     "multilingual": Whether the propety can have several languages
 *     "hiddenFromInterface": When truthy, this property is supported and
 *         recorded by the server and included in responses, but the user cannot
 *         edit it directly using the frontend interface.
 *     "prerequisite": This setting must be set before any of the other settings
 *         make any sense / have their dynamicOptions available
 *
 ********************************************************************/
const searchKeys = {
    "key": "searchKeys",
    "type": "list.string",
    "group": "urlParamsGroup",
    "inputType": "text",
    "default": [],
    "defaultInsert": "",
    "title": "Parameter name",
    "description": "The names of the url parameter.",
    "multilingual": false,
    "hiddenFromInterface": true,
};
const searchValues = {
    "key": "searchValues",
    "type": "list.string",
    "group": "urlParamsGroup",
    "inputType": "text",
    "default": [],
    "defaultInsert": "",
    "title": "Parameter value",
    "description": "The value of the parameters.",
    "multilingual": false,
    "hiddenFromInterface": true,
};
const previewView = {
    "key": "previewView",
    "type": "json",
    "jsonFormat": { "zoom": "number", "lat": "number", "lon": "number" },
    "inputType": "text",
    "default": null,
    "title": "Asset preview view",
    "description": "A JSON encoded view object",
    "multilingual": false,
    "hiddenFromInterface": true,
};
const description = {
    "key": "description",
    "type": "string",
    "inputType": "text",
    "default": null,
    "title": "Alternative text",
    "description": "A text description of the resource for accessibility.",
    "multilingual": true,
};
const attribution = {
    "key": "attribution",
    "type": "string",
    "inputType": "text",
    "default": null,
    "title": "Attribution",
    "description": "Copyright attribution for this resource.",
    "multilingual": true,
};
const label = {
    "key": "label",
    "type": "string",
    "inputType": "text",
    "default": null,
    "title": "Control label",
    "description": "The label to show for this layer in the map's layer control.",
    "multilingual": true,
};
const fileUploadUrl = {
    "key": "url",
    "type": "fileUploadUrl",
    "inputType": "file",
    "default": null,
    "title": "Resource url",
    "description": "A url pointing to the resource.",
    "multilingual": false,
    "prerequisite": true,
    "hiddenFromInterface": true,
};
const url = {
    "key": "url",
    "type": "url",
    "inputType": "url",
    "default": null,
    "title": "Resource url",
    "description": "A url pointing to the resource / server.",
    "multilingual": false,
    "prerequisite": true,
    "showInPreview": true,
    "hiddenFromInterface": true,
};
const textUrl = {
    "key": "url",
    "type": "url",
    // Service URLs may contain necessary, non-URI-encoded sequences such as "{x}"
    // etc. as placeholders for OpenLayers. We must thus use the raw user input and
    // can't use "inputType": "url" which sanitizes and URIencodes the URL.
    // OpenLayers is smart enough to not do anything with e.g. "javascript:..." urls
    // and we also separately test that the URL is a URL and that the protocol is 
    "inputType": "text",
    "default": null,
    "title": "Service url",
    "description": "A url pointing to the map server.",
    "hiddenFromInterface": true,
    "prerequisite": true,
    "multilingual": false,
};
const visibleZoomRange = {
    "key": "visibleZoomRange",
    "type": "list.integer",
    "inputType": "range",
    "default": [0, 25],
    "limits": [0, 25],
    "ticks": [1, 4, 10, 18],
    "tickLabels": ["Globe", "Country", "City", "City block"],
    "title": "Visible zoom range",
    "description": "The range of zoom levels for which this layer should be visible.",
    "multilingual": false,
    "hiddenFromInterface": true,
};
const nativeZoomRange = {
    "key": "nativeZoomRange",
    "type": "list.integer",
    "inputType": "range",
    "default": [1, 20],
    "limits": [0, 25],
    "ticks": [1, 4, 10, 18],
    "tickLabels": ["Globe", "Country", "City", "City block"],
    "title": "Server zoom range",
    "description": "The range of zoom levels for which the map server produces tiles.",
    "multilingual": false,
};
const layerOpacity = {
    "key": "layerOpacity",
    "type": "number",
    "inputType": "slider",
    "default": 1,
    "limits": [0, 1],
    "step": 0.01,
    "ticks": [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
    "title": "Layer opacity",
    "description": "The opacity of the layer, between 0 for see-through and 1 for fully opaque.",
    "multilingual": false,
};
const subdomains = {
    "key": "subdomains",
    "type": "string",
    "inputType": "text",
    "default": null,
    "title": "Subdomains",
    "description": "A comma-separated list of custom subdomains to use if the map server url contains a subdomain part.",
    "multilingual": false,
};
const tms = {
    "key": "tms",
    "type": "boolean",
    "inputType": "switch",
    "default": false,
    "title": "TMS",
    "description": "Select if this is a TMS server (inverted y-axis).",
    "multilingual": false,
    "hiddenFromInterface": true,
};
const version = {
    "key": "version",
    "type": "string",
    "inputType": "autoFill",
    "default": null,
    "title": "Version",
    "description": "WMS server protocol version",
    "hiddenFromInterface": true,
    "multilingual": false,
};
export const image = new AssetType({
    name: "image",
    group: "images",
    icon: ["fal", "image"],
    title: "Image",
    description: "An image file",
    propertiesList: [
        {
            ...fileUploadUrl,
            "fileType": "image",
        },
        description,
        attribution,
        {
            "key": "imageExtentMercator",
            "type": "list.number",
            "inputType": "text",
            "default": null,
            "title": "Image extent",
            "description": "Used for georeferencing an image",
            "multilingual": false,
            "hiddenFromInterface": true,
        }
    ],
});
export const audio = new AssetType({
    name: "audio",
    group: "audio",
    icon: ["fal", "volume-up"],
    title: "Audio",
    description: "An audio file",
    propertiesList: [
        {
            ...fileUploadUrl,
            "fileType": "audio",
        },
        description,
        attribution,
    ],
});
export const video = new AssetType({
    name: "video",
    group: "video",
    icon: ["fal", "video"],
    title: "Video",
    description: "A video file",
    hiddenFromInterface: true,
    propertiesList: [
        {
            ...fileUploadUrl,
            "fileType": "video",
        },
        description,
        attribution,
    ],
});
export const doxument = new AssetType({
    name: "document",
    group: "documents",
    icon: ["fal", "file-alt"],
    title: "Document",
    description: "A text document.",
    propertiesList: [
        {
            ...fileUploadUrl,
            "fileType": "document",
        },
        description,
    ],
});
export const bingmap = new AssetType({
    name: "bingmap",
    group: "maps",
    icon: ["fab", "bing"],
    iconText: "BING",
    title: "Bing map",
    description: "Settings for a Bing map",
    propertiesList: [
        {
            "key": "apiKey",
            "type": "string",
            "inputType": "text",
            "default": null,
            "title": "Bing API key",
            "description": "A Bing Maps API Key. Get your API key at <a href='http://www.bingmapsportal.com/' target='_blank'>bingmapsportal.com</a>.",
            "hiddenFromInterface": true,
            "prerequisite": true,
            "multilingual": false,
        },
        label,
        {
            "key": "imagerySet",
            "type": "string",
            "inputType": "ownOption",
            "valueOptions": [
                "RoadOnDemand",
                "CanvasDark",
                "CanvasGray",
                "Aerial",
                "AerialWithLabelsOnDemand",
            ],
            "valueOptionTexts": [
                "Roads",
                "Roads (dark)",
                "Roads (light)",
                "Aerial",
                "Aerial with a roads",
            ],
            "default": "RoadOnDemand",
            "title": "Imagery set",
            "description": "The imagery set to use for this Bing map.",
            "multilingual": false,
            "showInPreview": true,
        }, {
            "key": "customStyle",
            "type": "string",
            "inputType": "text",
            "default": null,
            "title": "Custom style",
            "description": "A URL formatted custom style as described in <a href='https://docs.microsoft.com/en-us/bingmaps/articles/custom-map-styles-in-bing-maps' target='_blank'>Bing maps' documentation</a> ",
            "multilingual": true,
        },
        layerOpacity,
        // visibleZoomRange,
        previewView,
    ],
});
export const mapbox = new AssetType({
    name: "mapbox",
    group: "maps",
    icon: ["fab", "mapbox"],
    iconText: "XYZ",
    title: "Mapbox basemap",
    description: "Settings for a Mapbox style",
    propertiesList: [
        textUrl,
        label,
        layerOpacity,
        nativeZoomRange,
        // visibleZoomRange, 
        previewView,
    ],
});
export const maptiler = new AssetType({
    name: "maptiler",
    group: "maps",
    icon: ["fab", "maptiler"],
    iconText: "MapTiler",
    title: "MapTiler basemap",
    description: "Settings for a MapTiler map",
    propertiesList: [
        textUrl,
        label,
        // layerOpacity,
        // nativeZoomRange,
        // visibleZoomRange, 
        previewView,
    ],
});
export const xyzmap = new AssetType({
    name: "xyzmap",
    group: "maps",
    icon: ["fal", "globe"],
    iconText: "XYZ",
    title: "XYZ basemap",
    description: "Settings for an XYZ map",
    propertiesList: [
        textUrl,
        label,
        attribution,
        layerOpacity,
        nativeZoomRange,
        // visibleZoomRange, 
        subdomains,
        tms,
        previewView,
    ],
});
export const wmsmap = new AssetType({
    name: "wmsmap",
    group: "maps",
    icon: ["fal", "globe"],
    iconText: "WMS",
    title: "WMS layer",
    description: "Settings for an external WMS layer",
    parallelGroups: [{
            group: "urlParamsGroup",
            title: "URL Parameters",
            description: "Groups corresponding to the separate url parameters.",
            hiddenFromInterface: true,
        }],
    propertiesList: [
        url,
        label,
        attribution,
        {
            "key": "serverType",
            "type": "string",
            "inputType": "ownOption",
            "valueOptions": [
                "carmentaserver",
                "geoserver",
                "mapserver",
                "qgis",
                "",
            ],
            "valueOptionTexts": [
                "Carmenta Server",
                "GeoServer",
                "MapServer",
                "QGIS",
                "Other",
            ],
            "default": "geoserver",
            "title": "Server type",
            "description": "Some server types have vendor parameters beyond the WMS specification. Identify your server type correctly to make the best use of it.",
            "multilingual": false,
        }, {
            "key": "layers",
            "type": "list.string",
            "inputType": "dynamicOptionMany",
            "default": null,
            "title": "Layers",
            "description": "The layers to use for this WMS map.",
            "multilingual": false,
            "showInPreview": true,
        },
        {
            "key": "tileSize",
            "type": "number",
            "inputType": "integer",
            "min": 32,
            "default": null,
            "title": "Tile size",
            "description": "Choose the size in pixels of (square shaped) tiles to be requested from the map server.",
            "multilingual": false,
        },
        layerOpacity,
        visibleZoomRange,
        {
            "key": "format",
            "type": "list.string",
            "inputType": "dynamicOption",
            "default": null,
            "title": "Tile format",
            "description": "The image format for the tiles. It is highly recommended choosing 'image/png' if available.",
            "multilingual": false,
        }, {
            "key": "transparent",
            "type": "boolean",
            "inputType": "switch",
            "default": null,
            "title": "Transparent tiles",
            "description": "Ask the WMS server to serve tiles with a transparent background.",
            "multilingual": false,
        },
        version,
        tms,
        {
            "key": "crs",
            "type": "list.string",
            "inputType": "dynamicOption",
            "default": null,
            "title": "Available CRSs",
            "description": "The CRSs available for this server",
            "hiddenFromInterface": true,
            "multilingual": false,
        }, {
            "key": "identify",
            "type": "boolean",
            "inputType": "autoFill",
            "default": null,
            "title": "Feature info",
            "description": "Does the WMS server support JSON based getFeatureInfo.",
            "hiddenFromInterface": true,
            "multilingual": false,
        },
        previewView,
        searchKeys,
        searchValues,
    ],
});
export const wmtsmap = new AssetType({
    name: "wmtsmap",
    group: "maps",
    icon: ["fal", "globe"],
    iconText: "WMTS",
    title: "WMTS layer",
    description: "Settings for an external WMTS layer",
    parallelGroups: [{
            group: "urlParamsGroup",
            title: "URL Parameters",
            description: "Groups corresponding to the separate url parameters.",
            hiddenFromInterface: true,
        }],
    propertiesList: [
        url,
        label,
        attribution,
        {
            "key": "layers",
            "type": "list.string",
            "inputType": "dynamicOption",
            "default": null,
            "title": "Layer",
            "description": "The layer to use for this WMTS map.",
            "multilingual": false,
            "showInPreview": true,
        }, {
            "key": "matrixSet",
            "type": "list.string",
            "inputType": "dynamicOption",
            "default": null,
            "title": "MatrixSet",
            "description": "The matrixSet to use for this WMTS map.",
            "multilingual": false,
            "showInPreview": true,
        },
        layerOpacity,
        // visibleZoomRange, 
        previewView,
        searchKeys,
        searchValues,
    ],
});
export const wfsmap = new AssetType({
    name: "wfsmap",
    group: "maps",
    icon: ["fal", "globe"],
    iconText: "WFS",
    title: "WFS layer",
    description: "Settings for an external WFS layer",
    parallelGroups: [{
            group: "urlParamsGroup",
            title: "URL Parameters",
            description: "Groups corresponding to the separate url parameters.",
            hiddenFromInterface: true,
        }],
    propertiesList: [
        url,
        label,
        attribution,
        {
            "key": "feature",
            "type": "list.string",
            "inputType": "dynamicOptionMany",
            "default": null,
            "title": "Feature",
            "description": "The feature to import from this WFS service.",
            "multilingual": false,
            "showInPreview": true,
        }, {
            "key": "visibleZoomRange",
            "type": "list.integer",
            "inputType": "range",
            "default": [0, 25],
            "limits": [0, 25],
            "ticks": [1, 4, 10, 18],
            "tickLabels": ["Globe", "Country", "City", "City block"],
            "title": "Visible zoom range",
            "description": "The range of zoom levels for which this layer should be visible.",
            "multilingual": false,
            // "hiddenFromInterface": true,
        }, {
            "key": "idKey",
            "type": "string",
            "inputType": "dynamicOption",
            "default": null,
            "title": "ID key",
            "description": "If the features' properties contain a value that you want to use as the response number in \"Clickable map objects\" responses, enter the value's key here. The values for the key must be integers. If the key is not found or if any individual feature's value is empty, an incremental ID starting at 0 will be used for those features.",
            "multilingual": false,
        }, {
            // DEPRECIATED
            // DEPRECIATED
            "key": "hasProperIds",
            "type": "boolean",
            "inputType": "automatic",
            "default": null,
            "title": "Has proper IDs",
            "description": "This WFS has proper ID keys and is suitable for use as a \"Clickable map objects\" target.",
            "multilingual": false,
            "hiddenFromInterface": true,
            // DEPRECIATED
            // DEPRECIATED
        }, {
            "key": "labelKey",
            "type": "string",
            "inputType": "dynamicOption",
            "default": null,
            "title": "Label key",
            "description": "If the features' properties contain a value that you want to use as a label, enter the value's key here. If the key is not found or if any individual feature's value is empty, the \"Control label\" setting will be used.",
            "multilingual": true,
        }, {
            "key": "showLabel",
            "type": "boolean",
            "inputType": "switch",
            "default": false,
            "title": "Show feature label",
            "description": "Select this to show a label on the map for this feature.",
            "multilingual": false,
        }, {
            "key": "declutterLabels",
            "type": "boolean",
            "inputType": "switch",
            "default": false,
            "title": "Declutter labels",
            "description": "Select this to hide overlapping labels on the map. Note that for point like features, this will introduce a small drawing delay when the map is zoomed.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelPlacement",
            "type": "string",
            "inputType": "ownOption",
            "valueOptions": [
                "point",
                "line",
            ],
            "valueOptionTexts": [
                "At center",
                "On line",
            ],
            "default": "point",
            "title": "Map label placement",
            "description": "Choose whether to display the label at the center of the feature or on the outline. Note that the line placement option only works if the feature has a long enough straight section to place the label.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelSize",
            "type": "number",
            "inputType": "integer",
            "min": 0,
            "default": null,
            "title": "Label size",
            "description": "Choose the font size for the label. If this is not set, the default value 12 is used.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelRotation",
            "type": "number",
            "inputType": "degrees",
            "default": 0,
            "title": "Label rotation",
            "description": "Use the slider to rotate the label orientation. Positive values will rotate the label clockwise. If this is not set the default value 0 is used.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelOffsetX",
            "type": "number",
            "inputType": "integer",
            "default": null,
            "title": "Label horizontal offset",
            "description": "Use this to offset the label horizontally. Positive values shift the label to the right. If this is not set the default values 20 and 0 will be used for point features and line/area features respectively",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelOffsetY",
            "type": "number",
            "inputType": "integer",
            "default": null,
            "title": "Label vertical offset",
            "description": "Use this to offset the label vertically. Positive values shift the label down. If this is not set the default values 0 and -2 will be used for point features and line/area features respectively",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "fontColor",
            "type": "string",
            "inputType": "color",
            "default": "#000000",
            "title": "Font color",
            "description": "The color to use for the label text.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelFillColor",
            "type": "string",
            "inputType": "color",
            "default": "#ffffff",
            "title": "Label color",
            "description": "The color to use for the label.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelBorderColor",
            "type": "string",
            "inputType": "color",
            "default": "#000000",
            "title": "Label color",
            "description": "The color to use for the label border.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelFillOpacity",
            "type": "number",
            "inputType": "slider",
            "default": 0.3,
            "limits": [0, 1],
            "step": 0.01,
            "ticks": [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
            "title": "Label opacity",
            "description": "The opacity of label, between 0 for see-through and 1 for fully opaque.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "point",
            "type": "boolean",
            "inputType": "switch",
            "default": true,
            "title": "Draw points",
            "description": "Whether to draw the points in this feature as circles on the map.",
            "multilingual": false,
        }, {
            "key": "pointRadius",
            "type": "number",
            "inputType": "integer",
            "min": 0,
            "default": 10,
            "title": "Point radius",
            "description": "The radius of the circle to draw for points in this feature.",
            "multilingual": false,
        }, {
            "key": "stroke",
            "type": "boolean",
            "inputType": "switch",
            "default": true,
            "title": "Draw outlines",
            "description": "Whether to draw the line segments of the feature.",
            "multilingual": false,
        }, {
            "key": "strokeWeight",
            "type": "number",
            "inputType": "integer",
            "min": 0,
            "default": 1,
            "title": "Stroke weight",
            "description": "The stroke width for drawing line segments.",
            "multilingual": false,
        }, {
            "key": "strokeColor",
            "type": "string",
            "inputType": "color",
            "default": "#d61313ff",
            "title": "Stroke color",
            "description": "The color to use for drawing line segments.",
            "multilingual": false,
        }, {
            "key": "strokeOpacity",
            "type": "number",
            "inputType": "slider",
            "default": 1,
            "limits": [0, 1],
            "step": 0.01,
            "ticks": [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
            "title": "Stroke opacity",
            "description": "The opacity of line segments, between 0 for see-through and 1 for fully opaque.",
            "multilingual": false,
        }, {
            "key": "fill",
            "type": "boolean",
            "inputType": "switch",
            "default": true,
            "title": "Fill outlines",
            "description": "Whether to fill outlined segments of the feature.",
            "multilingual": false,
        }, {
            "key": "fillColor",
            "type": "string",
            "inputType": "color",
            "default": "#d61313ff",
            "title": "Fill color",
            "description": "The color to use for filling out the feature.",
            "multilingual": false,
        }, {
            "key": "fillOpacity",
            "type": "number",
            "inputType": "slider",
            "default": 0.33,
            "limits": [0, 1],
            "step": 0.01,
            "ticks": [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
            "title": "Fill opacity",
            "description": "The opacity of the fill, between 0 for see-through and 1 for fully opaque.",
            "multilingual": false,
        },
        version,
        {
            "key": "srsName",
            "type": "string",
            "inputType": "autoFill",
            "default": null,
            "title": "SRS name",
            "description": "The SRS to use for this server",
            "hiddenFromInterface": true,
            "multilingual": false,
        },
        previewView,
        searchKeys,
        searchValues,
    ],
});
// export const imageMap = new AssetType({
//     name: "imagemap",
//     group: "maps",
//     icon: ["fal", "image"],
//     title: "Image map",
//     description: "An image file",
//     propertiesList: [
//     url, 
//     {        
//         "key": "description",      
//         "type": "string",      
//         "inputType": "text",       
//         "default": null,       
//         "title": "Alternative text",        
//         "description": "A text description of the image for accessibility.",     
//         "multilingual": true,
//     }, 
//     attribution],
// });
// export const tridify = new AssetType({
//     name: "tridify",
//     group: "models",
//     icon: ["fal", "file-archive"],
//     title: "Tridify Model",
//     description: "A Tridify model",
//     hiddenFromInterface: true,
//     propertiesList: [{
//         "key": "url",
//         "type": "url",
//         "inputType": "url",
//         "default": null,
//         "title": "Model url",
//         "description": "A url pointing to the model that should be used.",
//         "multilingual": true,
//     }, 
//     label],
// });
export const geotiff = new AssetType({
    name: "geotiff",
    group: "maps",
    icon: ["fal", "image"],
    iconText: "TIFF",
    title: "GeoTIFF",
    description: "Geo-referenced TIFF image",
    hiddenFromInterface: false,
    // atm it's not necessary to restrict access to GeoTIFFs by groupId
    // restrictGroupIdsConfig: "geoTiffAssetPermittedGroupIds",
    propertiesList: [
        {
            ...fileUploadUrl,
            "fileType": "geotiff",
        },
        label,
        attribution,
        visibleZoomRange,
        previewView,
        {
            "key": "convertToRGB",
            "type": "boolean",
            "inputType": "switch",
            "default": false,
            "title": "Convert to RGB",
            "description": "Convert bands to RGB",
            "multilingual": false,
        },
    ],
});
export const shapefile = new AssetType({
    name: "shapefile",
    group: "maps",
    icon: ["fal", "file-archive"],
    iconText: "SHP",
    title: "ESRI Shapefile",
    description: "A shapefile in .zip format",
    parallelGroups: [{
            group: "featureGroup",
            title: "Features",
            labelKey: "featureLabel",
            description: "Feature groups corresponding to the separate sub-files.",
        }],
    propertiesList: [
        {
            ...fileUploadUrl,
            "fileType": "shapefile",
        },
        label,
        attribution,
        // DEPRECIATED
        // DEPRECIATED
        {
            "key": "combineLayers",
            "type": "boolean",
            "inputType": "switch",
            "default": false,
            "title": "Combine layers",
            "description": "If there are more than one layer, combine them in the layer switcher to a single switchable layer.",
            "multilingual": false,
            "hiddenFromInterface": true,
        },
        // DEPRECIATED
        // DEPRECIATED
        {
            // DEPRECIATED
            // DEPRECIATED
            "key": "hasProperIds",
            "type": "boolean",
            "inputType": "automatic",
            "default": null,
            "title": "Has proper IDs",
            "description": "This shapefile asset has proper ID keys and is suitable for use as a \"Clickable map objects\" target.",
            "multilingual": false,
            "hiddenFromInterface": true,
            // DEPRECIATED
            // DEPRECIATED
        }, /* {
            "key": "crs",
            "type": "string",
            "inputType": "text",
            "default": "EPSG:4326",
            "title": "CRS",
            "description": "The CRS that the Shapefile is in.",
            "multilingual": true,
        },*/
        {
            "key": "fileName",
            "type": "list.string",
            "group": "featureGroup",
            "inputType": "text",
            "default": [],
            "defaultInsert": "",
            "title": "File names",
            "description": "The names of the shapefiles.",
            "multilingual": true,
            "hiddenFromInterface": true,
        }, {
            "key": "featureLabel",
            "type": "list.string",
            "group": "featureGroup",
            "inputType": "text",
            "default": [],
            "defaultInsert": "",
            "title": "Feature label",
            "description": "The label to show for this sub-layer in the map's layer control.",
            "multilingual": true,
            "autoFill": "string:key:fileName",
        }, {
            "key": "visibleZoomRange",
            "type": "list.list.number",
            "group": "featureGroup",
            "inputType": "range",
            "default": [],
            "defaultInsert": [0, 25],
            "limits": [0, 25],
            "ticks": [1, 4, 10, 18],
            "tickLabels": ["Globe", "Country", "City", "City block"],
            "title": "Fill opacity",
            "description": "The opacity of the fill, between 0 for see-through and 1 for fully opaque.",
            "multilingual": false,
            "hiddenFromInterface": true,
        }, {
            "key": "idKey",
            "type": "list.string",
            "group": "featureGroup",
            "inputType": "dynamicOption",
            "default": [],
            "defaultInsert": null,
            "title": "ID key",
            "description": "If the shapefile's properties contain a value that you want to use as the response number in \"Clickable map objects\" responses, enter the value's key here. The values for the key must be integers. If the key is not found or if any individual feature's value is empty, an incremental ID starting at 0 will be used for those features.",
            "multilingual": false,
        }, {
            "key": "labelKey",
            "type": "list.string",
            "group": "featureGroup",
            "inputType": "dynamicOption",
            "default": [],
            "defaultInsert": null,
            "title": "Label key",
            "description": "If the shapefile's data table contains a column that you want to use as a label, enter the column name here. If the column is not found or if any individual feature's entry is empty, the \"Feature label\" setting will be used.",
            "multilingual": true,
        }, {
            "key": "showLabel",
            "type": "list.boolean",
            "group": "featureGroup",
            "inputType": "switch",
            "default": [],
            "defaultInsert": false,
            "title": "Show feature label",
            "description": "Select this to show a label on the map for this feature.",
            "multilingual": false,
        }, {
            "key": "declutterLabels",
            "type": "list.boolean",
            "group": "featureGroup",
            "inputType": "switch",
            "default": [],
            "defaultInsert": false,
            "title": "Declutter labels",
            "description": "Select this to hide overlapping labels on the map. Note that for point like features, this will introduce a small drawing delay when the map is zoomed.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelPlacement",
            "type": "list.string",
            "group": "featureGroup",
            "inputType": "ownOption",
            "valueOptions": [
                "point",
                "line",
            ],
            "valueOptionTexts": [
                "At center",
                "On line",
            ],
            "default": [],
            "defaultInsert": "point",
            "title": "Map label placement",
            "description": "Choose whether to display the label at the center of the feature or on the outline. Note that the line placement option only works if the feature has a long enough straight section to place the label.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelSize",
            "type": "list.number",
            "group": "featureGroup",
            "inputType": "integer",
            "min": 0,
            "default": [],
            "defaultInsert": 12,
            "title": "Label size",
            "description": "Choose the font size for the label. If this is not set, the default value 12 is used.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelRotation",
            "type": "list.number",
            "group": "featureGroup",
            "inputType": "degrees",
            "default": [],
            "defaultInsert": 0,
            "title": "Label rotation",
            "description": "Use the slider to rotate the label orientation. Positive values will rotate the label clockwise. If this is not set the default value 0 is used.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelOffsetX",
            "type": "list.number",
            "group": "featureGroup",
            "inputType": "integer",
            "default": [],
            "defaultInsert": null,
            "title": "Label horizontal offset",
            "description": "Use this to offset the label horizontally. Positive values shift the label to the right. If this is not set the default values 20 and 0 will be used for point features and line/area features respectively",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelOffsetY",
            "type": "list.number",
            "group": "featureGroup",
            "inputType": "integer",
            "default": [],
            "defaultInsert": null,
            "title": "Label vertical offset",
            "description": "Use this to offset the label vertically. Positive values shift the label down. If this is not set the default values 0 and -2 will be used for point features and line/area features respectively",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "fontColor",
            "type": "list.string",
            "group": "featureGroup",
            "inputType": "color",
            "default": [],
            "defaultInsert": "#000000",
            "title": "Font color",
            "description": "The color to use for the label text.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelFillColor",
            "type": "list.string",
            "group": "featureGroup",
            "inputType": "color",
            "default": [],
            "defaultInsert": "#ffffff",
            "title": "Label color",
            "description": "The color to use for the label.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelBorderColor",
            "type": "list.string",
            "group": "featureGroup",
            "inputType": "color",
            "default": [],
            "defaultInsert": "#000000",
            "title": "Label color",
            "description": "The color to use for the label border.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelFillOpacity",
            "type": "list.number",
            "group": "featureGroup",
            "inputType": "slider",
            "default": [],
            "defaultInsert": 0.3,
            "limits": [0, 1],
            "step": 0.01,
            "ticks": [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
            "title": "Label opacity",
            "description": "The opacity of the label, between 0 for see-through and 1 for fully opaque.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "pointRadius",
            "type": "list.number",
            "group": "featureGroup",
            "inputType": "integer",
            "min": 0,
            "default": [],
            "defaultInsert": 10,
            "title": "Point radius",
            "description": "The radius of the circle to draw for points in this feature.",
            "multilingual": false,
        }, {
            "key": "strokeWeight",
            "type": "list.number",
            "group": "featureGroup",
            "inputType": "integer",
            "min": 0,
            "default": [],
            "defaultInsert": 1,
            "title": "Stroke weight",
            "description": "The stroke width for drawing line segments.",
            "multilingual": false,
        }, {
            "key": "strokeColor",
            "type": "list.string",
            "group": "featureGroup",
            "inputType": "color",
            "default": [],
            "title": "Stroke color",
            "description": "The color to use for drawing line segments.",
            "multilingual": false,
            "autoFill": "palette",
        }, {
            "key": "strokeOpacity",
            "type": "list.number",
            "group": "featureGroup",
            "inputType": "slider",
            "default": [],
            "defaultInsert": 1,
            "limits": [0, 1],
            "step": 0.01,
            "ticks": [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
            "title": "Stroke opacity",
            "description": "The opacity of line segments, between 0 for see-through and 1 for fully opaque.",
            "multilingual": false,
        }, {
            "key": "fillColor",
            "type": "list.string",
            "group": "featureGroup",
            "inputType": "color",
            "default": [],
            "title": "Fill color",
            "description": "The color to use for filling out the feature.",
            "multilingual": false,
            "autoFill": "palette",
        }, {
            "key": "fillOpacity",
            "type": "list.number",
            "group": "featureGroup",
            "inputType": "slider",
            "default": [],
            "defaultInsert": 0.33,
            "limits": [0, 1],
            "step": 0.01,
            "ticks": [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
            "title": "Fill opacity",
            "description": "The opacity of the fill, between 0 for see-through and 1 for fully opaque.",
            "multilingual": false,
        }, {
            "key": "strokeDash",
            "type": "list.list.number",
            "group": "featureGroup",
            "inputType": "integer",
            "default": [0, 0],
            "title": "Dashed stroke",
            "description": "The size (in pixels) of the dash segments for the line.",
            "multilingual": false,
        },
        previewView,
        {
            "key": "attributeStyles",
            "type": "list.json", // see geojsonAttributeStyleValidator
            "hiddenFromInterface": true,
            "default": [],
            "multilingual": false,
            "group": "featureGroup",
            "title": "Select attribute",
            "description": "Choose an attribute to style",
        },
    ],
});
export const geojson = new AssetType({
    name: "geojson",
    group: "maps",
    icon: ["fal", "file"],
    iconText: "JSON",
    title: "GeoJSON",
    description: "A geoJSON text file",
    propertiesList: [
        {
            ...fileUploadUrl,
            "fileType": "geojson",
        },
        label,
        attribution,
        // visibleZoomRange,
        {
            "key": "idKey",
            "type": "string",
            "inputType": "dynamicOption",
            "default": null,
            "title": "ID key",
            "description": "If the GeoJSON's properties contain a value that you want to use as the response number in \"Clickable map objects\" responses, enter the value's key here. The values for the key must be integers. If the key is not found or if any individual feature's value is empty, an incremental ID starting at 0 will be used for those features.",
            "multilingual": false,
        }, {
            // DEPRECIATED
            // DEPRECIATED
            "key": "hasProperIds",
            "type": "boolean",
            "inputType": "automatic",
            "default": null,
            "title": "Has proper IDs",
            "description": "This geoJSON asset has proper ID keys and is suitable for use as a \"Clickable map objects\" target.",
            "multilingual": false,
            "hiddenFromInterface": true,
            // DEPRECIATED
            // DEPRECIATED
        }, {
            "key": "labelKey",
            "type": "string",
            "inputType": "dynamicOption",
            "default": null,
            "title": "Label key",
            "description": "If the GeoJSON's properties contain a value that you want to use as a label, enter the value's key here. If the key is not found or if any individual feature's value is empty, the \"Control label\" setting will be used.",
            "multilingual": true,
        }, {
            "key": "showLabel",
            "type": "boolean",
            "inputType": "switch",
            "default": false,
            "title": "Show feature label",
            "description": "Select this to show a label on the map for this feature.",
            "multilingual": false,
        }, {
            "key": "declutterLabels",
            "type": "boolean",
            "inputType": "switch",
            "default": false,
            "title": "Declutter labels",
            "description": "Select this to hide overlapping labels on the map. Note that for point like features, this will introduce a small drawing delay when the map is zoomed.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelPlacement",
            "type": "string",
            "inputType": "ownOption",
            "valueOptions": [
                "point",
                "line",
            ],
            "valueOptionTexts": [
                "At center",
                "On line",
            ],
            "default": "point",
            "title": "Map label placement",
            "description": "Choose whether to display the label at the center of the feature or on the outline. Note that the line placement option only works if the feature has a long enough straight section to place the label.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelSize",
            "type": "number",
            "inputType": "integer",
            "min": 0,
            "default": null,
            "title": "Label size",
            "description": "Choose the font size for the label. If this is not set, the default value 12 is used.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelRotation",
            "type": "number",
            "inputType": "degrees",
            "default": 0,
            "title": "Label rotation",
            "description": "Use the slider to rotate the label orientation. Positive values will rotate the label clockwise. If this is not set the default value 0 is used.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelOffsetX",
            "type": "number",
            "inputType": "integer",
            "default": null,
            "title": "Label horizontal offset",
            "description": "Use this to offset the label horizontally. Positive values shift the label to the right. If this is not set the default values 20 and 0 will be used for point features and line/area features respectively",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelOffsetY",
            "type": "number",
            "inputType": "integer",
            "default": null,
            "title": "Label vertical offset",
            "description": "Use this to offset the label vertically. Positive values shift the label down. If this is not set the default values 0 and -2 will be used for point features and line/area features respectively",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "fontColor",
            "type": "string",
            "inputType": "color",
            "default": "#000000",
            "title": "Font color",
            "description": "The color to use for the label text.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelFillColor",
            "type": "string",
            "inputType": "color",
            "default": "#ffffff",
            "title": "Label color",
            "description": "The color to use for the label.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelBorderColor",
            "type": "string",
            "inputType": "color",
            "default": "#000000",
            "title": "Label color",
            "description": "The color to use for the label border.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "labelFillOpacity",
            "type": "number",
            "inputType": "slider",
            "default": 0.3,
            "limits": [0, 1],
            "step": 0.01,
            "ticks": [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
            "title": "Label opacity",
            "description": "The opacity of label, between 0 for see-through and 1 for fully opaque.",
            "multilingual": false,
            "showIfProperty": {
                "key": "showLabel",
                "value": [true],
            },
        }, {
            "key": "pointRadius",
            "type": "number",
            "inputType": "integer",
            "min": 0,
            "default": 10,
            "title": "Point radius",
            "description": "The radius of the circle to draw for points in this feature.",
            "multilingual": false,
        }, {
            "key": "strokeWeight",
            "type": "number",
            "inputType": "integer",
            "min": 0,
            "default": 1,
            "title": "Stroke weight",
            "description": "The stroke width for drawing line segments.",
            "multilingual": false,
        }, {
            "key": "strokeColor",
            "type": "string",
            "inputType": "color",
            "default": "#d61313ff",
            "title": "Stroke color",
            "description": "The color to use for drawing line segments.",
            "multilingual": false,
        }, {
            "key": "strokeOpacity",
            "type": "number",
            "inputType": "slider",
            "default": 1,
            "limits": [0, 1],
            "step": 0.01,
            "ticks": [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
            "title": "Stroke opacity",
            "description": "The opacity of line segments, between 0 for see-through and 1 for fully opaque.",
            "multilingual": false,
        }, {
            "key": "strokeDash",
            "type": "list.number",
            "inputType": "integer",
            "default": [0, 0],
            "title": "Dashed stroke",
            "description": "The size (in pixels) of the dash segments for the line.",
            "multilingual": false,
        }, {
            "key": "fillColor",
            "type": "string",
            "inputType": "color",
            "default": "#d61313ff",
            "title": "Fill color",
            "description": "The color to use for filling out the feature.",
            "multilingual": false,
        }, {
            "key": "fillOpacity",
            "type": "number",
            "inputType": "slider",
            "default": 0.33,
            "limits": [0, 1],
            "step": 0.01,
            "ticks": [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
            "title": "Fill opacity",
            "description": "The opacity of the fill, between 0 for see-through and 1 for fully opaque.",
            "multilingual": false,
        },
        previewView,
        {
            "key": "attributeStyles",
            "type": "json",
            "jsonFormat": { "[foo]": "bar" }, // not used: see geojsonAttributeStyleValidator
            "hiddenFromInterface": true,
            "default": null,
            "multilingual": false,
            "title": "Select attribute",
            "description": "Choose an attribute to style",
        },
    ],
});
export const vectortileservice = new AssetType({
    name: "vectortileservice",
    group: "maps",
    icon: ["fal", "globe"],
    iconText: "vectortiles",
    title: "Vector Tile Service",
    description: "Settings for an ESRI vector tile layer",
    hiddenFromInterface: false,
    propertiesList: [
        {
            ...url,
            description: "URL which points to the ArcGIS Vector Tile resource, excluding a possible suffix like .pbf"
        },
        {
            "key": "styleUrl",
            "type": "url",
            "inputType": "text",
            "default": null,
            "title": "Style url",
            "description": "An optional url pointing to a custom style JSON based on the Mapbox vector tile style specification.",
            "hiddenFromInterface": false,
            "prerequisite": false,
            "multilingual": false,
        },
        label,
        attribution,
        layerOpacity,
        {
            "key": "visibleZoomRange",
            "type": "list.integer",
            "inputType": "range",
            "default": [0, 25],
            "limits": [0, 25],
            "ticks": [1, 4, 10, 18],
            "tickLabels": ["Globe", "Country", "City", "City block"],
            "title": "Visible zoom range",
            "description": "The range of zoom levels for which this layer should be visible.",
            "multilingual": false,
        },
        previewView,
    ],
});
//# sourceMappingURL=assetType.js.map